import * as constants from "@/config/constants";
import { decode } from "he";
import { path, pathOr, prop } from "ramda";
import { NumericFormat } from "react-number-format";
import { stripHtml } from "string-strip-html";

const MAX_STARS = 5;
const POOR_RATING = 1;
const FAIR_RATING = 2;
const AVERAGE_RATING = 3;
const GOOD_RATING = 4;
const ROUNDING_CEILING = 0.78;
const ROUNDING_FLOOR = 0.21;

export const returnImageURL = (assets, ASSET_ID) => {
	const assetContainingURL = assets.filter(
		(asset) => path(["sys", "id"], asset) === ASSET_ID,
	);
	return path(["0", "fields", "file", "url"], assetContainingURL);
};

export const filterCategoryInfoForDisplay = (categoryArray) =>
	categoryArray.filter((entry) =>
		pathOr(false, ["fields", "categoryName"], entry),
	);

export const determineIfFilteringAllowed = (isTargetedPath, vertical) => {
	const isTargetedPathFilteringAllowed = pathOr(
		false,
		[vertical, "isTargetedPathFilteringAllowed"],
		constants.SITE_INFORMATION,
	);
	const isNonTargetedPathFilteringAllowed = pathOr(
		false,
		[vertical, "isNonTargetedPathFilteringAllowed"],
		constants.SITE_INFORMATION,
	);
	const isFilteringAllowed = isTargetedPath
		? isTargetedPathFilteringAllowed
		: isNonTargetedPathFilteringAllowed;
	if (isFilteringAllowed === undefined) {
		throw new Error(
			`Unable to determine if filtering is allowed for vertical: ${vertical} when isTargetedPath: ${isTargetedPath}`,
		);
	}
	return isFilteringAllowed;
};

export const numToCurrency = (number, options = {}) => {
	const defaultOptions = {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
	};

	/* Don't add decimals to whole numbers e.g. 4 -> $4 not 4 -> $4.00*/
	if (number % 1 === 0) {
		defaultOptions.minimumFractionDigits = 0;
	}
	const abbreviatedOptions = { notation: "compact", compactDisplay: "short" };
	const allOptions = { ...defaultOptions, ...abbreviatedOptions };
	const appliedOptions = options.abbreviate ? allOptions : defaultOptions;
	const currencyFormatter = new Intl.NumberFormat("en-US", appliedOptions);
	return currencyFormatter.format(number);
};

export const removeCharacterEntities = (containsEntities) => {
	if (Array.isArray(containsEntities))
		return containsEntities.map((str) => decode(str));
	else if (typeof containsEntities === "string")
		return decode(containsEntities);
	else
		throw new Error(
			"Neither a string or array of strings was passed to removeCharacterEntities helper function",
		);
};

export const formatServiceRightColumns = (company, columnInformation) => {
	if (!company)
		throw new Error(
			"company not passed to formatServiceRightColumns helper function",
		);
	if (!columnInformation)
		throw new Error(
			"columnInformation not passed to formatServiceRightColumns helper function",
		);

	const generateColumn = (label, value) => ({ label, value });

	return columnInformation.map((column) => {
		const { fields, label } = column;

		/* Boolean to determine if we're using more than one Contentful field for a value */
		const isFieldsArray = Array.isArray(fields);

		const buildPath = (targetField) => ["fields", targetField];

		let contentfulFieldValues = isFieldsArray
			? fields.map((field) => path(buildPath(field), company))
			: path(buildPath(fields), company);

		/* Checking to see if we need to apply any conversions to contentfulField values before formatting */
		if (column.hasOwnProperty("preFormatterConversions")) {
			const { preFormatterConversions } = column;

			if (preFormatterConversions.hasOwnProperty("currencyConversions")) {
				const { currencyConversions } = preFormatterConversions;
				if (currencyConversions.isConverted) {
					contentfulFieldValues = numToCurrency(contentfulFieldValues, {
						abbreviate: prop("isAbbreviated", currencyConversions),
					});
				}
			}

			if (preFormatterConversions.hasOwnProperty("numbers")) {
				const { numbers } = preFormatterConversions;
				if (numbers.addCommaSeparator) {
					contentfulFieldValues = (
						<NumericFormat
							value={contentfulFieldValues}
							displayType={"text"}
							thousandSeparator={","}
							renderText={(value) => value}
						/>
					);
				}
			}
		}

		const fieldValues = column.formatter
			? column.formatter(contentfulFieldValues)
			: contentfulFieldValues;
		return generateColumn(label, fieldValues);
	});
};

export const convertRatingToStars = (reviewRating) => {
	// rounds up if decimal place is >= 0.8
	// ROUNDING_CEILING is defined as .78 due to JS numbers being stored as 64 bit doubles; thus, 4.8 % 1 = 7.99999... instead of 0.8
	// Similarly, ROUNDING_FLOOR is defined as .21
	const roundFloorCheck = (reviewRating) => reviewRating % 1 < ROUNDING_CEILING;

	const roundedRating = roundFloorCheck(reviewRating)
		? Math.floor(reviewRating)
		: Math.ceil(reviewRating);
	const ratingRemainder = reviewRating - roundedRating;
	let colorQualities = [];

	let colorQuality;
	if (roundedRating <= POOR_RATING) {
		colorQuality = "poor";
	} else if (roundedRating <= FAIR_RATING) {
		colorQuality = "fair";
	} else if (roundedRating <= AVERAGE_RATING) {
		colorQuality = "average";
	} else if (roundedRating <= GOOD_RATING) {
		colorQuality = "good";
	} else {
		colorQuality = "excellent";
	}

	for (let i = 0; i < roundedRating; i++) {
		colorQualities.push(colorQuality);
	}

	// push half stars if decimal is between 0.3 and 0.7
	if (ratingRemainder > ROUNDING_FLOOR && ratingRemainder < ROUNDING_CEILING) {
		colorQualities.push(colorQualities[roundedRating - 1] + "Half");
	}

	// push default for any remaining indices or if rating is undefined
	if (colorQualities.length < MAX_STARS) {
		for (let i = colorQualities.length; i < MAX_STARS; i++) {
			colorQualities.push("default");
		}
	}

	return colorQualities;
};

export const formatMediaAlphaBenefits = (
	mediaAlphaBenefits,
	applyStringFormatting = false,
) => {
	/* case insensitive replacements e.g. "New york Life" would be replaced with "New York Life" */
	const replacements = [
		"Allstate",
		"Direct Auto",
		"Esurance",
		"Farmers",
		"Liberty Mutual",
		"Mercury Insurance",
		"New York Life",
		"Progressive",
		"Safe Driving Bonus®",
		"SelectQuote",
		"The General",
	];

	const states = [
		"Alabama",
		"Alaska",
		"American Samoa",
		"Arizona",
		"Arkansas",
		"California",
		"Colorado",
		"Connecticut",
		"Delaware",
		"District of Columbia",
		"Florida",
		"Georgia",
		"Guam",
		"Hawaii",
		"Idaho",
		"Illinois",
		"Indiana",
		"Iowa",
		"Kansas",
		"Kentucky",
		"Louisiana",
		"Maine",
		"Marshall Islands",
		"Maryland",
		"Massachusetts",
		"Michigan",
		"Minnesota",
		"Mississippi",
		"Missouri",
		"Montana",
		"Nebraska",
		"Nevada",
		"New Hampshire",
		"New Jersey",
		"New Mexico",
		"New York",
		"North Carolina",
		"North Dakota",
		"Northern Mariana Islands",
		"Ohio",
		"Oklahoma",
		"Oregon",
		"Palau",
		"Pennsylvania",
		"Puerto Rico",
		"Rhode Island",
		"South Carolina",
		"South Dakota",
		"Tennessee",
		"Texas",
		"Utah",
		"Vermont",
		"Virgin Island",
		"Virginia",
		"Washington",
		"West Virginia",
		"Wisconsin",
		"Wyoming",
	];

	const allReplacements = [...states, ...replacements];

	/* HTML string of list elements e.g. "<li>item 1</li><li>item 2</li>"*/
	const withoutUnorderedList = stripHtml(mediaAlphaBenefits, {
		onlyStripTags: ["ul", "ol"],
	}).result;
	/* Split into array of strings for any tag*/
	const withoutListTags = withoutUnorderedList
		.split(/<[^>]*>/g)
		.filter((val) => val !== "");

	const formatStrings = (str) => {
		const newWordsArray = [];
		/*  break string into array of words*/
		const allWordsArray = str.split(" ");

		/* Iterate through all words in the array*/
		for (let i = 0; i < allWordsArray.length; i++) {
			/* create a new array of letters for each word */
			const letterArray = [...allWordsArray[i]];

			if (i === 0) {
				// capitalize first letter of first word
				newWordsArray[i] = [
					...letterArray[0].toUpperCase(),
					...letterArray.slice(1),
				].join("");
			} else {
				/* check if making the word lower case changes anything. If so, part of it must be in caps*/
				if (allWordsArray[i].toLowerCase() !== allWordsArray[i]) {
					/* if there's a single capital letter return it in lower case */
					if (letterArray.length === 1) {
						newWordsArray[i] = allWordsArray[i].toLowerCase();
					} else {
						const isAcronym =
							letterArray[0].toUpperCase() === letterArray[0] &&
							letterArray[1].toUpperCase() === letterArray[1];
						if (isAcronym) {
							newWordsArray[i] = allWordsArray[i];
						} else newWordsArray[i] = allWordsArray[i].toLowerCase();
					}
				} else {
					newWordsArray.push(allWordsArray[i].toLowerCase());
				}
			}
		}

		const applyReplacements = (str) => {
			let nextString = str;
			allReplacements.forEach((word) => {
				const regEx = new RegExp(word, "ig");
				nextString = nextString.replace(regEx, word);
			});
			return nextString;
		};

		const partiallyFormattedString = newWordsArray.join(" ");
		const completelyFormattedString = removeCharacterEntities(
			applyReplacements(partiallyFormattedString),
		);
		return completelyFormattedString;
	};

	if (applyStringFormatting) {
		return withoutListTags.map((str) => formatStrings(str.trim()));
	} else return removeCharacterEntities(withoutListTags);
};
